import { useNavigate, useParams } from "react-router-dom";
import { useCallback, useEffect, useMemo, useState } from "react";
import React from "react";
import { VideoShareViewResult } from "api/interfaces/videoshareInterface.interface";
import { useMutation, useQuery } from "react-query";
import {
  deleteVideoShare,
  getVideoShareViewByVideoId,
  updateVideoShareContent,
} from "api/videoshareAPI";
import ViewVSPresenter from "./ViewVSPresenter";
import useApiError from "hook/useApiError";
import { CustomError } from "api/interfaces/commonInterface.interface";
import { useAppSelector } from "redux/hooks";
import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import NoAvailable from "components/templates/nopage/NoAvailable";
import { useMenu } from "hook/useMenu";
import { checkTimezone } from "utils/timeUtil";
import useStatusMutation from "hook/useStatusMutation";
import { useQueryClient } from "react-query";
export default function ViewVSContainer(): JSX.Element {
  const { handleErrorNotify, notifySuccess, lang } = useApiError();

  const initialState = {
    status: "open",
    sharing: false,
    title: "",
    cameraName: "",
    recorderName: "",
    mergedSystemName: "",
    type: "",
    time: "",
    username: "",
    url: "",
    startTimeMs: 0,
    creationTimeMs: 0,
    endTimeMs: 0,
    durationMs: 0,
    size: 0,
    date: "",
    tags: [],
    timeZone: "",
    description: "",
    uploadStatus: "",
  };

  const navigate = useNavigate();
  const { videoId } = useParams();

  const [isLoading, setIsLoading] = useState(true);
  const [isDelete, setIsDelete] = useState(false);

  const [state, setState] = useState<VideoShareViewResult>(initialState);
  const [videoUrl, setVideoUrl] = useState<string>("");

  const [isDisabledAdd, setIsDisabledAdd] = useState<boolean>(
    state.uploadStatus === "success" && state.sharing
  );

  const playerReference = React.useRef(null);

  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );
  const { menuNavigation } = useMenu({});

  const playerReady = (player: any) => {
    playerReference.current = player;

    // handling video player
    player.on("waiting", () => {
      console.log("Video Player is waiting");
    });
    player.on("dispose", () => {
      console.log("Video player will dispose");
    });
  };

  const { error } = useQuery(
    ["videoshareView", videoId],
    () => {
      if (videoId !== undefined) {
        return getVideoShareViewByVideoId({
          videoId: videoId,
        });
      }
    },
    {
      retry: 0,
      refetchOnWindowFocus: false,
      onSuccess: (res: any) => {
        // console.log(res, "video share view");
        if (res.error === 0) {
          setState(res.result);
          setVideoUrl(res.result.url);
        }
      },
      onError: (e: any) => {
        console.log(e, "error");
      },
    }
  );

  const onDelete = (value: boolean) => {
    setIsDelete(value);
  };

  const onConfirmDelete = () => {
    mutationDeleteVideoShare.mutate({
      videoId,
    });
  };

  const mutationDeleteVideoShare = useMutation(deleteVideoShare, {
    onSuccess: (res: any) => {
      if (res.status === 200) {
        // queryClient.invalidateQueries("videoShare");
        // navigate(-1);
        //BUG [RND-361]
        notifySuccess(lang.label.videoshare.notify.delete.success);
        menuNavigation({ to: "/videoShare" });
      }
    },
    onError: (err: CustomError) => {
      handleErrorNotify(err, lang.label.videoshare.notify.delete.fail);
    },
  });

  useEffect(() => {
    if (state) {
      setIsLoading(false);

      setIsDisabledAdd(state.uploadStatus === "success" && state.sharing);
    }
  }, [state]);

  const onUpdateContentValues = useCallback((key: string, value: any) => {
    setState((prev) => {
      return {
        ...prev,
        [key]: value,
      };
    });
  }, []);

  const videoTimezone = useMemo(() => {
    const serverTimezone = checkTimezone(
      state.displayTimeZone ?? state.timeZone
    );
    if (serverTimezone === "") {
      return selectedAccount.timezone;
    }
    return serverTimezone;
  }, [selectedAccount.timezone, state.displayTimeZone, state.timeZone]);
  const [tags, setTags] = useState<string[]>(() =>
    (state?.tags || []).map((tag) => tag.tag)
  );
  const [isEditTags, setIsEditTags] = useState<boolean>(false);
  const onToggleEditTags = useCallback(() => {
    setIsEditTags((prev) => {
      if (!prev) {
        setTags((prev) => (state?.tags || []).map((tag) => tag.tag));
      }
      return !prev;
    });
  }, [state?.tags]);
  const queryClient = useQueryClient();
  const mutationUpdateVS = useStatusMutation(updateVideoShareContent, {
    onSuccess: (data: any) => {
      notifySuccess(lang.label.videoshare.notify.save.success);
      queryClient.invalidateQueries("videoShareHashTag");
      queryClient.invalidateQueries("videoshareView");
      setIsEditTags(false);
    },
    onError: (err: CustomError) => {
      handleErrorNotify(err, lang.label.videoshare.notify.save.fail);
    },
  });
  const onSaveTags = useCallback(() => {
    mutationUpdateVS.mutate({
      videoId: videoId,
      key: "tags",
      value: tags,
    });
  }, [mutationUpdateVS, tags, videoId]);

  if (error) {
    return (
      <NoAvailable
        onReturn={() => menuNavigation({ to: "/videoShare" })}
        buttonLabel="Return to Video Share"
        message="This video is no longer available"
      />
    );
  }
  return (
    <ViewVSPresenter
      videoId={videoId}
      videoUrl={videoUrl}
      state={state}
      isLoading={isLoading}
      isDelete={isDelete}
      isDisabledAdd={isDisabledAdd}
      playerReady={playerReady}
      onDelete={onDelete}
      onConfirmDelete={onConfirmDelete}
      onUpdateContentValues={onUpdateContentValues}
      selectedAccount={selectedAccount}
      videoTimezone={videoTimezone}
      tags={tags}
      setTags={setTags}
      isEditTags={isEditTags}
      onToggleEditTags={onToggleEditTags}
      onSaveTags={onSaveTags}
    />
  );
}
